import {
  Box,
  Card,
  CardContent,
  Container,
  Fade,
  Grid,
  Slide,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import type { Skill } from '../../constants/types';
import { useOptimizedData } from '../../hooks/useOptimizedData';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'transparent',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  background: 'rgba(34, 40, 49, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(5),
  border: '1px solid rgba(0, 173, 181, 0.2)',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transition: 'width 0.8s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'rgba(0, 173, 181, 0.1)',
    borderRadius: 2,
  },
  '&.animate::after': {
    width: 80,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  maxWidth: 600,
  margin: `${theme.spacing(6)}px auto ${theme.spacing(8)}px auto`,
  fontStyle: 'italic',
  position: 'relative',
  lineHeight: 1.6,
  paddingTop: theme.spacing(2),
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
    fontSize: '1rem',
  },
}));

const TabsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  width: '100%',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#00ADB5',
    height: 3,
  },
  '& .MuiTabs-scrollButtons': {
    color: theme.palette.primary.main,
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
  '& .MuiTabs-scroller': {
    [theme.breakpoints.down('md')]: {
      overflow: 'auto !important',
    },
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    minWidth: 120,
    [theme.breakpoints.down('md')]: {
      minWidth: 100,
      fontSize: '1rem',
      padding: theme.spacing(1.5, 2),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 90,
      fontSize: '0.9rem',
      padding: theme.spacing(1.5, 1.5),
    },
    '&.Mui-selected': {
      color: '#00ADB5',
    },
  },
}));

const SkillCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 62, 70, 0.9)',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(0, 173, 181, 0.2)',
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(2),
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 173, 181, 0.15)',
    border: '1px solid rgba(0, 173, 181, 0.4)',
    background: 'rgba(57, 62, 70, 0.95)',
  },
}));

const SkillIcon = styled('img')(({ theme }) => ({
  width: 56,
  height: 56,
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  filter: 'brightness(0.9) saturate(0.9)',
  flexShrink: 0,
  [theme.breakpoints.up('sm')]: {
    width: 64,
    height: 64,
  },
  [theme.breakpoints.up('md')]: {
    width: 70,
    height: 70,
  },
  '&:hover': {
    transform: 'scale(1.1)',
    filter: 'brightness(1) saturate(1) drop-shadow(0 0 12px rgba(0, 173, 181, 0.6))',
  },
}));

const SkillName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '0.9rem',
  textAlign: 'center',
  fontFamily: 'Gilroy, sans-serif',
  lineHeight: 1.3,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.1rem',
  },
}));

// Updated tech icons with official logos where available
const techIcons: { [key: string]: string } = {
  // Frontend
  'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
  'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
  'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
  'Tailwind CSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg',
  'Material UI': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/materialui/materialui-original.svg',
  'HTML5': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg',
  'CSS3': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
  
  
  // Backend
  'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
  'Java': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  'Spring Boot': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg',
  'MySQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
  'Redis': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg',
  'GraphDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/neo4j/neo4j-original.svg',
  
  // Tools & DevOps
  'AWS': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg',
  'Jenkins': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg',
  'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  'CI/CD': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
  'Serverless Framework': 'https://serverlessframework.com/images/logo-serverless-framework-logo-light-solid.svg',
  'SAML': 'https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/saml.svg',
  'OIDC': 'https://raw.githubusercontent.com/simple-icons/simple-icons/develop/icons/openid.svg',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`skills-tabpanel-${index}`}
      aria-labelledby={`skills-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const SkillsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [animateTitle, setAnimateTitle] = useState(false);
  const { getSkillsByCategory } = useOptimizedData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Delay the title animation slightly for better effect
          setTimeout(() => setAnimateTitle(true), 300);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('skills');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const skillCategories = [
    { label: 'Frontend', skills: getSkillsByCategory('frontend') },
    { label: 'Backend', skills: getSkillsByCategory('backend') },
    { label: 'Tools & DevOps', skills: getSkillsByCategory('tools') },
  ];

  return (
    <SectionContainer id="skills">
      <Container maxWidth="lg">
        <ContentWrapper>
          <Slide direction="up" in={true} mountOnEnter unmountOnExit>
            <div>
              <SectionTitle 
                variant="h2" 
                className={animateTitle ? 'animate' : ''}
                sx={{ 
                  fontSize: { xs: '2.25rem', md: '3rem' }, 
                  fontWeight: 'bold', 
                  mb: 2 
                }}
              >
                Technical Skills
              </SectionTitle>
              <SectionSubtitle variant="subtitle1" sx={{ textAlign: 'center', margin: '0 auto' }}>
                Technologies and tools I use to bring ideas to life
              </SectionSubtitle>
            </div>
          </Slide>
          <Box mt={2}/>
          <Fade in={true} timeout={1000}>
            <Box>
              <TabsContainer>
                <StyledTabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant={isMobile ? "scrollable" : "standard"}
                  scrollButtons={isMobile ? "auto" : false}
                  allowScrollButtonsMobile={isMobile}
                  centered={!isMobile}
                >
                  {skillCategories.map((category, index) => (
                    <Tab
                      key={index}
                      label={category.label}
                      id={`skills-tab-${index}`}
                      aria-controls={`skills-tabpanel-${index}`}
                    />
                  ))}
                </StyledTabs>
              </TabsContainer>

              {skillCategories.map((category, index) => (
                <TabPanel key={index} value={activeTab} index={index}>
                  <Grid container spacing={2} justifyContent="center">
                    {category.skills.map((skill: Skill, skillIndex: number) => (
                      <Grid item xs={6} sm={4} md={3} lg={2.4} key={skillIndex}>
                        <Slide
                          direction="up"
                          in={activeTab === index}
                          timeout={300 + skillIndex * 100}
                        >
                          <SkillCard>
                            <CardContent sx={{ padding: '20px !important', textAlign: 'center' }}>
                              <SkillIcon 
                                src={techIcons[skill.name] || `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" fill="#00adb5" rx="8"/><text x="32" y="40" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="24" font-weight="bold">${skill.name.charAt(0)}</text></svg>`)}`} 
                                alt={skill.name}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  // Prevent infinite loop by checking if we're already using the fallback
                                  if (!target.src.startsWith('data:image/svg+xml')) {
                                    target.src = `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><rect width="64" height="64" fill="#00adb5" rx="8"/><text x="32" y="40" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="24" font-weight="bold">${skill.name.charAt(0)}</text></svg>`)}`;
                                  }
                                }}
                              />
                              <SkillName>{skill.name}</SkillName>
                            </CardContent>
                          </SkillCard>
                        </Slide>
                      </Grid>
                    ))}
                  </Grid>
                </TabPanel>
              ))}
            </Box>
          </Fade>
        </ContentWrapper>
      </Container>
    </SectionContainer>
  );
};

export default SkillsSection; 